'use client';

import { Flex } from '@ironhack/design-system2/components/flex';
import { Text } from '@ironhack/design-system2/components/text';
import { type ReactElement, useState } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';

import { FormThankYou } from '@/app/components/client/form-thank-you';
import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { type ValidationMessageSet } from '@/lib/utils/form-validations';
import { type NameValidationMessages } from '@/lib/utils/form-validations/schemas/name-schema';
import { Form as FormComponent } from '../../components/form';
import type {
  DatoButtonBlock,
  DatoCampus,
  DatoCourse,
} from '@/lib/datocms/types';
import { type ThankYouSession } from '@/hooks/use-thank-you-session';

export type Form = {
  campuses: Array<Pick<DatoCampus, 'code' | 'isShownOnWebsite' | 'name'>>;
  campusLabel: string;
  campusPlaceholder: string;
  campusRequiredMessage: string;
  buttonText: string;
  chooseBetween: string;
  completeFields: string;
  courseLabel: string;
  coursePlaceholder: string;
  courseRequiredMessage: string;
  courses: Array<Pick<DatoCourse, 'code' | 'name'>>;
  courseTypeLabel: string;
  emailLabel: string;
  emailPlaceholder: string;
  emailValidationMessages: ValidationMessageSet;
  errorMessage: string;
  firstNameLabel: string;
  firstNamePlaceholder: string;
  firstNameValidationMessages: NameValidationMessages;
  inPersonOption: string;
  inquiryLabel: string;
  inquiryPlaceholder: string;
  inquiryRequiredMessage: string;
  lastNameLabel: string;
  lastNamePlaceholder: string;
  lastNameValidationMessages: NameValidationMessages;
  legalText: StructuredTextGraphQlResponse;
  newsletterLabel: string;
  onlineOption: string;
  phoneInvalidMessage: string;
  phoneLabel: string;
  phonePlaceholder: string;
  phoneRequiredMessage: string;
  phoneStep2Text: string;
  step2Subtitle: string;
  step2Title: string;
  subtitle: string;
  thankYouText: string;
  thankYouTitle: string;
  title: string;
};

type Props = {
  data: {
    contactForm: Form;
    preStudentPortalButton: DatoButtonBlock | null;
    thankYouButton: [DatoButtonBlock];
  };
};

export const Form = (props: Props): ReactElement => {
  const {
    data: { contactForm, thankYouButton, preStudentPortalButton },
  } = props;
  const {
    title,
    step2Title,
    step2Subtitle,
    subtitle,
    thankYouText,
    thankYouTitle,
  } = contactForm;

  const [isStep2, setIsStep2] = useState(false);
  const [thankYouSession, setThankYouSession] = useState<ThankYouSession>();

  const handleSuccess = (formThankYouSession: ThankYouSession) => {
    setThankYouSession(formThankYouSession);
  };

  return (
    <ModuleWrapper
      innerProps={{ direction: 'column', alignItems: 'center' }}
      moduleFormat={{
        background: 'light',
        spacingBottom: '40',
        spacingTop: '40',
      }}
      outerProps={{}}
    >
      {thankYouSession ? (
        <FormThankYou
          bg={[null, null, 'background.white']}
          borderRadius={[null, null, '2xl']}
          button={thankYouButton}
          id="contactSuccess"
          p={[null, null, 5]}
          preStudentPortalButton={preStudentPortalButton}
          text={thankYouText}
          thankYouSession={thankYouSession}
          title={thankYouTitle}
          w={['full', null, '400px']}
        />
      ) : (
        <Flex
          bg={['background.light', null, 'background.white']}
          borderRadius="16px"
          boxShadow={[null, null, 'lg']}
          direction="column"
          p={[0, null, 5]}
          w={['full', null, '680px']}
        >
          {!isStep2 && (
            <>
              <Text as="h1" textStyle={['3xl', null, '5xl']}>
                {title}
              </Text>
              <Text mt={4} textStyle="l">
                {subtitle}
              </Text>
            </>
          )}
          {isStep2 && (
            <>
              <Text textStyle="lBold">{step2Title}</Text>
              <Text mt={2} textStyle="l">
                {step2Subtitle}
              </Text>
            </>
          )}
          <FormComponent
            contactForm={contactForm}
            mt={4}
            onStep2={() => setIsStep2(true)}
            onSuccess={handleSuccess}
          />
        </Flex>
      )}
    </ModuleWrapper>
  );
};
